import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { UserRole } from '@/domain';
import { useAuth } from '@/features/auth/AuthContext';
import { PageSkeleton } from '@/components/ui/skeleton';

/**
 * Route guards.
 *
 * These decide what renders, not what is permitted — the API is the enforcement point for
 * every rule in FRD §3.2. A guard that is bypassed reveals a page shell, not data.
 */

/** Requires a session. Remembers where the user was heading so login can return them. */
export function ProtectedRoute() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // The session probe is still running: redirecting now would bounce a signed-in user
  // to the login screen on every page refresh.
  if (isLoading) return <PageSkeleton />;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // FR-AUTH-008 — a forced password change blocks everything else.
  if (user?.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  return <Outlet />;
}

/** Restricts a branch of the route tree to specific roles. */
export function RoleRoute({ roles }: { roles: UserRole[] }) {
  const { user, isLoading } = useAuth();

  if (isLoading) return <PageSkeleton />;

  if (!user || !roles.includes(user.role)) {
    return <Navigate to="/forbidden" replace />;
  }

  return <Outlet />;
}

/** Keeps a signed-in user away from the login screen. */
export function PublicOnlyRoute() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <PageSkeleton />;
  if (isAuthenticated) return <Navigate to="/" replace />;

  return <Outlet />;
}
